import { dec, formatMoney, formatQuantity, sum, ZERO } from "@bl/domain";
import { Link, useNavigate } from "@tanstack/react-router";
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight } from "lucide-react";
import { Fragment, type ReactNode, type RefObject, useEffect, useRef, useState } from "react";
import type { Item, Location } from "../api";
import { CategoryChip } from "../components/ui";
import {
  findSpec,
  formLabel,
  makerText,
  regionText,
  relative,
  showAvailable,
  showOnHand,
  showQuantity,
  showSpec,
  showUnitCost,
} from "../format";
import { useLayout } from "../layout/LayoutProvider";
import { INVENTORY_COLUMNS, type InventoryColumnId, type SortKey } from "../layout/layout";
import { soonestOf, useShortFrom } from "../shortFrom";
import {
  brandsOf,
  formNameOf,
  formsOf,
  type InventoryRow,
  regionsOf,
  rowsOf,
  type Variety,
} from "./hopVarieties";
import {
  Available,
  FoldCaret,
  GroupToggle,
  type Inv,
  varietyOnHand,
  varietyWhere,
  whereText,
} from "./InventoryCards";

function Qty({ item, mode }: { item: Item; mode: "unit" | "pack" }) {
  return (
    <span className={`num ${item.onHand.negative ? "negative" : ""}`}>
      {showOnHand(item, mode)}
    </span>
  );
}
export function Table({
  inv,
  groups,
  locations,
  folded,
  onFold,
}: {
  inv: Inv;
  groups: { key: string; items: Item[] }[];
  locations: Location[];
  folded: (key: string) => boolean;
  onFold: (key: string) => void;
}) {
  const { layout, update } = useLayout();
  const navigate = useNavigate();
  const wrap = useRef<HTMLDivElement>(null);
  const head = useRef<HTMLTableSectionElement>(null);
  const lifted = useLiftedHead(wrap, head);
  const shortFrom = useShortFrom();
  const colorUnit = layout.preferences.colorUnit;
  const columns = inv.columns.filter((c) => c.visible);
  const sortable: Partial<Record<InventoryColumnId, SortKey>> = {
    name: "name",
    category: "category",
    onHand: "onHand",
    lastMovement: "lastMovement",
  };
  // Showing only hops, the maker is a hop's brands (Scott, 2026-10-02).
  const label = (id: InventoryColumnId) =>
    inv.category === "hop" && id === "manufacturer"
      ? "Brand"
      : (INVENTORY_COLUMNS.find((c) => c.id === id)?.label ?? id);
  // Hop varieties dropped down to their forms, by variety.
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const toggle = (key: string) =>
    setOpen((was) => {
      const next = new Set(was);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  // Every variety in a group at once, from its header (Tyler, 2026-10-02).
  const toggleAll = (keys: string[], opening: boolean) =>
    setOpen((was) => {
      const next = new Set(was);
      for (const key of keys) {
        if (opening) next.add(key);
        else next.delete(key);
      }
      return next;
    });
  const cell: Record<InventoryColumnId, (i: Item) => ReactNode> = {
    name: (i) => (
      <Link to="/items/$itemId" params={{ itemId: i.id }} onClick={(e) => e.stopPropagation()}>
        {i.displayName}
      </Link>
    ),
    form: (i) => formLabel(i),
    region: (i) => regionText(i),
    category: (i) => <CategoryChip category={i.category} />,
    onHand: (i) => <Qty item={i} mode={inv.quantityMode} />,
    planned: (i) =>
      dec(i.planned).eq(ZERO) ? (
        ""
      ) : (
        <span className="num">{showQuantity(i, i.planned, inv.quantityMode)}</span>
      ),
    available: (i) =>
      dec(i.planned).eq(ZERO) ? (
        ""
      ) : (
        <Available
          below={dec(i.available).lt(ZERO)}
          short={dec(i.available).lt(ZERO) ? shortFrom(i.id) : null}
        >
          {showAvailable(i, i.available, inv.quantityMode)}
        </Available>
      ),
    packs: (i) =>
      i.pack ? (
        <span className="num">{showOnHand(i, "pack")}</span>
      ) : (
        <span className="faint">none</span>
      ),
    locations: (i) => <span className="muted">{whereText(i, locations, inv.quantityMode)}</span>,
    lots: (i) => <span className="num">{i.onHand.lots}</span>,
    avgCost: (i) =>
      i.cost.averageOnHand ? (
        <span className="num">{showUnitCost(i, i.cost.averageOnHand, inv.quantityMode)}</span>
      ) : (
        ""
      ),
    value: (i) =>
      dec(i.cost.valueOnHand).eq(ZERO) ? (
        ""
      ) : (
        <span className="num">{formatMoney(i.cost.valueOnHand)}</span>
      ),
    manufacturer: (i) => makerText(i),
    sku: (i) => i.defaultSupplierSku ?? "",
    color: (i) => {
      const s = findSpec(i.specs, "color");
      return s ? showSpec(s, colorUnit).text : "";
    },
    alpha: (i) => {
      const s = findSpec(i.specs, "alpha_acid");
      return s ? showSpec(s, colorUnit).text : "";
    },
    lastMovement: (i) => <span className="muted">{relative(i.lastMovementAt)}</span>,
  };
  const numeric = new Set<InventoryColumnId>([
    "onHand",
    "planned",
    "available",
    "packs",
    "lots",
    "avgCost",
    "value",
  ]);
  const weighed = (v: Variety, canonical: string | ReturnType<typeof sum>) =>
    formatQuantity(canonical, { unit: v.items[0]?.displayUnit ?? "lb" });
  // A variety's row adds up its forms: what is on hand, where, its lots and value, and lists their
  // forms, brands and regions. Figures that differ by form, as alpha and cost do, stay on the forms.
  const varietyCell: Record<InventoryColumnId, (v: Variety) => ReactNode> = {
    name: (v) => (
      <button
        type="button"
        className="variety-toggle"
        aria-expanded={open.has(v.key)}
        onClick={(e) => {
          e.stopPropagation();
          toggle(v.key);
        }}
      >
        {open.has(v.key) ? (
          <ChevronDown size={16} aria-hidden />
        ) : (
          <ChevronRight size={16} aria-hidden />
        )}
        {v.name}
      </button>
    ),
    form: (v) => formsOf(v),
    region: (v) => regionsOf(v),
    category: (v) => <CategoryChip category={v.kind} />,
    onHand: (v) => (
      <span className={`num ${v.items.some((i) => i.onHand.negative) ? "negative" : ""}`}>
        {varietyOnHand(v, inv.quantityMode)}
      </span>
    ),
    // What the schedule plans of its forms together, and what that leaves available. A strain's are
    // the pitch's: the schedule plans what recipes ask for, and they ask for the yeast bought.
    planned: (v) => {
      if (v.kind === "yeast") return v.items[0] ? cell.planned(v.items[0]) : "";
      const total = sum(v.items.map((i) => i.planned));
      return total.eq(ZERO) ? "" : <span className="num">{weighed(v, total)}</span>;
    },
    available: (v) => {
      if (v.kind === "yeast") return v.items[0] ? cell.available(v.items[0]) : "";
      if (sum(v.items.map((i) => i.planned)).eq(ZERO)) return "";
      const total = sum(v.items.map((i) => i.available));
      return (
        <Available
          below={total.lt(ZERO)}
          short={total.lt(ZERO) ? soonestOf(v.items.map((i) => shortFrom(i.id))) : null}
        >
          {total.lt(ZERO) ? `${weighed(v, total.abs())} short` : weighed(v, total)}
        </Available>
      );
    },
    packs: () => "",
    locations: (v) => <span className="muted">{varietyWhere(v, locations, inv.quantityMode)}</span>,
    lots: (v) => <span className="num">{v.items.reduce((n, i) => n + i.onHand.lots, 0)}</span>,
    avgCost: () => "",
    value: (v) => {
      const total = sum(v.items.map((i) => i.cost.valueOnHand));
      return total.eq(ZERO) ? "" : <span className="num">{formatMoney(total)}</span>;
    },
    manufacturer: (v) => brandsOf(v),
    sku: () => "",
    color: () => "",
    alpha: () => "",
    lastMovement: (v) => (
      <span className="muted">
        {relative(
          v.items
            .map((i) => i.lastMovementAt)
            .filter((at): at is string => at !== null)
            .sort()
            .at(-1) ?? null,
        )}
      </span>
    ),
  };
  const itemRow = (i: Item, asForm = false) => (
    <tr
      key={i.id}
      className={`clickable ${asForm ? "form-row" : ""}`}
      onClick={() => navigate({ to: "/items/$itemId", params: { itemId: i.id } })}
    >
      {columns.map((c) => (
        <td
          key={c.id}
          className={`${numeric.has(c.id) ? "num" : ""} ${c.id === "name" ? "name-cell" : ""}`}
        >
          {asForm && c.id === "name" ? (
            <Link
              to="/items/$itemId"
              params={{ itemId: i.id }}
              onClick={(e) => e.stopPropagation()}
            >
              {formNameOf(i)}
            </Link>
          ) : (
            cell[c.id](i)
          )}
        </td>
      ))}
    </tr>
  );
  const rowOf = (r: InventoryRow) =>
    r.kind === "item" ? (
      itemRow(r.item)
    ) : (
      <Fragment key={r.variety.key}>
        <tr className="clickable variety-row" onClick={() => toggle(r.variety.key)}>
          {columns.map((c) => (
            <td
              key={c.id}
              className={`${numeric.has(c.id) ? "num" : ""} ${c.id === "name" ? "name-cell" : ""}`}
            >
              {varietyCell[c.id](r.variety)}
            </td>
          ))}
        </tr>
        {open.has(r.variety.key) ? r.variety.items.map((i) => itemRow(i, true)) : null}
      </Fragment>
    );
  /** A column's name, which sorts by it where it can, in the table and in the header lifted off it. */
  const headOf = (c: (typeof columns)[number], focusable = true) => {
    const key = sortable[c.id];
    if (!key) return label(c.id);
    const active = inv.sort.key === key;
    return (
      <button
        type="button"
        tabIndex={focusable ? undefined : -1}
        onClick={() =>
          update((l) => ({
            ...l,
            inventory: {
              ...l.inventory,
              sort: { key, dir: active && inv.sort.dir === "asc" ? "desc" : "asc" },
            },
          }))
        }
      >
        {label(c.id)}
        {active ? inv.sort.dir === "asc" ? <ArrowUp size={13} /> : <ArrowDown size={13} /> : null}
      </button>
    );
  };
  return (
    <div className="table-wrap" ref={wrap}>
      <table className={`table ${lifted ? "head-lifted" : ""}`}>
        <thead ref={head}>
          <tr>
            {columns.map((c) => {
              const key = sortable[c.id];
              const sorted = key !== undefined && inv.sort.key === key;
              return (
                <th
                  key={c.id}
                  className={numeric.has(c.id) ? "num" : undefined}
                  aria-sort={
                    sorted ? (inv.sort.dir === "asc" ? "ascending" : "descending") : undefined
                  }
                >
                  {headOf(c)}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => {
            const rows = rowsOf(g.items);
            const varieties = rows.flatMap((r) => (r.kind === "variety" ? [r.variety.key] : []));
            const allOpen = varieties.length > 0 && varieties.every((k) => open.has(k));
            return (
              <Fragment key={g.key}>
                {inv.groupByCategory ? (
                  <tr className="group-row" onClick={() => onFold(g.key)}>
                    <td colSpan={columns.length}>
                      <div className="group-bar">
                        <GroupToggle
                          category={g.key}
                          count={rows.length}
                          folded={folded(g.key)}
                          caret={false}
                          onToggle={() => onFold(g.key)}
                        />
                        {varieties.length > 0 && !folded(g.key) ? (
                          <button
                            type="button"
                            className="link-button expand-all"
                            aria-expanded={allOpen}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleAll(varieties, !allOpen);
                            }}
                          >
                            {allOpen ? "Collapse all" : "Expand all"}
                          </button>
                        ) : null}
                        {/* A click on it is a click on the row, which folds the category. */}
                        <FoldCaret folded={folded(g.key)} />
                      </div>
                    </td>
                  </tr>
                ) : null}
                {folded(g.key) ? null : rows.map(rowOf)}
              </Fragment>
            );
          })}
        </tbody>
      </table>
      {lifted ? (
        // A copy for the eye and the mouse; the table's own header row is the one read and tabbed to.
        <div className="table-float" aria-hidden style={{ left: lifted.left, width: lifted.width }}>
          <div className="table-float-row" style={{ transform: `translateX(${-lifted.scroll}px)` }}>
            {columns.map((c, n) => (
              <div
                key={c.id}
                className={`table-float-cell ${numeric.has(c.id) ? "num" : ""}`}
                style={{ width: lifted.cells[n] }}
              >
                {headOf(c, false)}
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
interface Lifted {
  /** Where the table is across the screen, and how far it is scrolled sideways. */
  left: number;
  width: number;
  scroll: number;
  /** Each column's width, as the table draws it. */
  cells: number[];
}
/**
 * The table's column names stay in sight (Tyler, 2026-10-03). In place they are the table's top row;
 * once that row reaches the bars at the top of the screen it lifts off into a long pill floating just
 * below them, its columns as wide as the table's, and drops back into place when the table is scrolled
 * back down. It lets go again as the table's last rows pass under it.
 */
function useLiftedHead(
  wrap: RefObject<HTMLDivElement | null>,
  head: RefObject<HTMLTableSectionElement | null>,
): Lifted | null {
  const [lifted, setLifted] = useState<Lifted | null>(null);
  useEffect(() => {
    const measure = () => {
      const w = wrap.current;
      const h = head.current;
      if (!w || !h) return;
      const root = getComputedStyle(document.documentElement);
      const bars =
        Number.parseFloat(root.getPropertyValue("--header-h")) +
        Number.parseFloat(root.getPropertyValue("--toolbar-h"));
      const line = bars + LIFT_GAP;
      const top = h.getBoundingClientRect();
      const table = w.getBoundingClientRect();
      if (top.top >= line || table.bottom < line + top.height * 2) {
        setLifted((was) => (was ? null : was));
        return;
      }
      const next: Lifted = {
        left: table.left,
        width: table.width,
        scroll: w.scrollLeft,
        cells: [...h.querySelectorAll("th")].map((th) => th.getBoundingClientRect().width),
      };
      setLifted((was) =>
        was &&
        was.left === next.left &&
        was.width === next.width &&
        was.scroll === next.scroll &&
        was.cells.join() === next.cells.join()
          ? was
          : next,
      );
    };
    measure();
    const w = wrap.current;
    window.addEventListener("scroll", measure, { passive: true });
    window.addEventListener("resize", measure);
    w?.addEventListener("scroll", measure, { passive: true });
    // The table changes size with no scroll when a category folds or a column is shown.
    const resized = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    if (w) resized?.observe(w);
    return () => {
      window.removeEventListener("scroll", measure);
      window.removeEventListener("resize", measure);
      w?.removeEventListener("scroll", measure);
      resized?.disconnect();
    };
  }, [wrap, head]);
  return lifted;
}
/** How far below the bars at the top the lifted header floats. */
const LIFT_GAP = 10;
